/**
 * DatePicker - Date selection component
 */

import NepaliCalendar from "../calendar/NepaliCalendar.js";
import EnglishCalendar from "../calendar/EnglishCalendar.js";
import NepaliDateConverter from "../converters/NepaliDateConverter.js";

class DatePicker {
  constructor(options = {}) {
    this.options = {
      calendar: "nepali",
      language: "en",
      format: "YYYY-MM-DD",
      placeholder: "Select date",
      minDate: null,
      maxDate: null,
      disabledDates: [],
      disabledDaysOfWeek: [],
      closeOnSelect: true,
      showInput: true,
      inline: false,
      onChange: null,
      onShow: null,
      onHide: null,
      ...options,
    };

    this.container = null;
    this.input = null;
    this.pickerContainer = null;
    this.calendar = null;
    this.selectedDate = null;
    this.isVisible = false;
    this.initialized = false;
  }

  /**
   * Initialize the date picker
   * @param {HTMLElement|string} element - Container element or selector
   */
  initialize(element) {
    if (typeof element === "string") {
      element = document.querySelector(element);
    }

    if (!element) {
      throw new Error("Container element not found");
    }

    this.container = element;
    this._createStructure();
    this._initializeCalendar();
    this._attachEventListeners();
    this.initialized = true;

    if (this.options.inline) {
      this.show();
    }
  }

  /**
   * Create picker structure with size support
   * @private
   */
  _createStructure() {
    // Handle input elements differently than container elements
    const isInputElement = this.container.tagName === "INPUT";

    if (isInputElement) {
      // Store reference to original input
      const originalInput = this.container;

      // Create wrapper div and insert it before the input
      const wrapper = document.createElement("div");
      wrapper.className = "ndtp-date-picker ndtp-input-wrapper";
      originalInput.parentNode.insertBefore(wrapper, originalInput);

      // Move the input into the wrapper
      wrapper.appendChild(originalInput);

      // Add the appropriate classes to the input
      originalInput.classList.add("ndtp-input");

      // Set the container to be the wrapper
      this.container = wrapper;

      // Use the original input as the display input
      this.input = originalInput;
    } else {
      // Original behavior for div containers
      this.container.innerHTML = "";
      this.container.classList.add("ndtp-date-picker");

      // Create input if needed
      if (this.options.showInput) {
        this.input = document.createElement("input");
        this.input.type = "text";
        this.input.className = "ndtp-input";
        this.input.placeholder = this.options.placeholder;
        this.input.readOnly = true;
        this.container.appendChild(this.input);
      }
    }

    // Apply size class
    if (this.options.size) {
      // Remove any existing size classes
      this.container.classList.remove(
        "ndtp-size-sm",
        "ndtp-size-md",
        "ndtp-size-lg"
      );
      // Add the specified size class
      this.container.classList.add(`ndtp-size-${this.options.size}`);
    }

    // Create picker container
    this.pickerContainer = document.createElement("div");
    this.pickerContainer.className = "ndtp-calendar-container";
    if (!this.options.inline) {
      this.pickerContainer.classList.add("ndtp-popup");
      this.pickerContainer.style.display = "none";
    }

    // Also apply size class to picker container for consistency
    if (this.options.size) {
      this.pickerContainer.classList.add(`ndtp-size-${this.options.size}`);
    }

    this.container.appendChild(this.pickerContainer);
  }

  /**
   * Initialize calendar
   * @private
   */
  _initializeCalendar() {
    const CalendarClass =
      this.options.calendar === "nepali" ? NepaliCalendar : EnglishCalendar;

    this.calendar = new CalendarClass({
      language: this.options.language,
      minDate: this.options.minDate,
      maxDate: this.options.maxDate,
      disabledDates: this.options.disabledDates,
      disabledDaysOfWeek: this.options.disabledDaysOfWeek,
      onSelect: (date) => {
        this.selectedDate = date;
        this._updateInput();

        if (this.options.onChange) {
          this.options.onChange(date);
        }

        if (this.options.closeOnSelect && !this.options.inline) {
          this.hide();
        }
      },
    });

    this.calendar.initialize(this.pickerContainer);
  }

  /**
   * Attach event listeners
   * @private
   */
  _attachEventListeners() {
    if (this.input && !this.options.inline) {
      // Show picker on input click
      this.input.addEventListener("click", (e) => {
        e.stopPropagation();
        this.show();
      });

      // Show picker on input focus
      this.input.addEventListener("focus", (e) => {
        e.stopPropagation();
        this.show();
      });
    }

    // Close on outside click
    this._handleOutsideClick = (e) => {
      if (!this.options.inline && this.isVisible) {
        const target = e.target;
        if (
          !this.container.contains(target) &&
          !this.pickerContainer.contains(target)
        ) {
          this.hide();
        }
      }
    };

    document.addEventListener("click", this._handleOutsideClick);

    // Close on escape key
    this._handleKeyDown = (e) => {
      if (e.key === "Escape" && this.isVisible && !this.options.inline) {
        this.hide();
      }
    };

    document.addEventListener("keydown", this._handleKeyDown);
  }

  /**
   * Update input value
   * @private
   */
  _updateInput() {
    if (!this.input || !this.selectedDate) return;

    const formattedDate = this.calendar.formatDate(
      this.selectedDate,
      this.options.format
    );
    this.input.value = formattedDate;
  }

  /**
   * Position the picker
   * @private
   */
  _positionPicker() {
    if (this.options.inline || !this.input) return;

    const inputRect = this.input.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    // Position below input by default
    let top = inputRect.bottom + scrollTop;
    let left = inputRect.left + scrollLeft;

    // Check if picker would go off screen bottom
    const pickerHeight = 300; // Approximate height of picker
    if (top + pickerHeight > window.innerHeight + scrollTop) {
      // Position above input
      top = inputRect.top + scrollTop - pickerHeight;
    }

    // Check if picker would go off screen right
    const pickerWidth = 280; // Approximate width of picker
    if (left + pickerWidth > window.innerWidth + scrollLeft) {
      // Align to right edge of input
      left = inputRect.right + scrollLeft - pickerWidth;
    }

    this.pickerContainer.style.position = "absolute";
    this.pickerContainer.style.top = `${top}px`;
    this.pickerContainer.style.left = `${left}px`;
    this.pickerContainer.style.zIndex = "1000";

    // Ensure picker is added to document body for proper positioning
    if (this.pickerContainer.parentNode !== document.body) {
      document.body.appendChild(this.pickerContainer);
    }
  }

  /**
   * Get selected date
   * @returns {Object|null} Selected date
   */
  getDate() {
    return this.selectedDate ? { ...this.selectedDate } : null;
  }

  /**
   * Set date
   * @param {Object} date - Date object
   */
  setDate(date) {
    if (!date) {
      this.clear();
      return;
    }

    if (!this.calendar.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    this.selectedDate = { ...date };
    if (this.calendar) {
      this.calendar.setSelectedDate(date);
      this.calendar.setViewDate(date);
    }
    this._updateInput();

    if (this.options.onChange) {
      this.options.onChange(this.selectedDate);
    }
  }

  /**
   * Clear selection
   */
  clear() {
    this.selectedDate = null;
    if (this.calendar) {
      this.calendar.setSelectedDate(null);
    }
    if (this.input) {
      this.input.value = "";
    }
    if (this.options.onChange) {
      this.options.onChange(null);
    }
  }

  /**
   * Show the picker
   */
  show() {
    if (this.isVisible || this.options.inline) return;

    this.pickerContainer.style.display = "block";
    this.isVisible = true;

    // Position the picker
    this._positionPicker();

    // Force a reflow to ensure the element is rendered
    this.pickerContainer.offsetHeight;

    if (this.options.onShow) {
      this.options.onShow();
    }
  }

  /**
   * Hide the picker
   */
  hide() {
    if (!this.isVisible || this.options.inline) return;

    this.pickerContainer.style.display = "none";
    this.isVisible = false;

    if (this.options.onHide) {
      this.options.onHide();
    }
  }

  /**
   * Parse date from string
   * @param {string} dateString - Date string
   * @returns {Object|null} Parsed date
   */
  parseDate(dateString) {
    if (!dateString) return null;
    return this.calendar.parseDate(dateString, this.options.format);
  }

  /**
   * Go to today
   */
  goToToday() {
    if (this.calendar) {
      this.calendar.today();
    }
  }

  /**
   * Convert date between calendars
   * @param {Object} date - Date to convert
   * @param {string} toCalendar - Target calendar ('nepali' or 'english')
   * @returns {Object} Converted date
   */
  convertDate(date, toCalendar) {
    if (!date) return null;

    if (this.options.calendar === toCalendar) {
      return date;
    }

    if (this.options.calendar === "nepali" && toCalendar === "english") {
      if (this.calendar instanceof NepaliCalendar) {
        return this.calendar.toAd(date);
      }
    } else if (this.options.calendar === "english" && toCalendar === "nepali") {
      if (this.calendar instanceof EnglishCalendar) {
        const converter = new NepaliDateConverter();
        return converter.adToBs(date);
      }
    }

    return date;
  }

  /**
   * Check if picker is visible
   * @returns {boolean}
   */
  isOpen() {
    return this.isVisible;
  }

  /**
   * Destroy the picker
   */
  destroy() {
    if (this.calendar) {
      this.calendar.destroy();
    }

    // Remove picker from body if it's there
    if (
      this.pickerContainer &&
      this.pickerContainer.parentNode === document.body
    ) {
      document.body.removeChild(this.pickerContainer);
    }

    if (this.container) {
      this.container.innerHTML = "";
      this.container.classList.remove("ndtp-date-picker");
    }

    // Remove event listeners
    if (this._handleOutsideClick) {
      document.removeEventListener("click", this._handleOutsideClick);
    }
    if (this._handleKeyDown) {
      document.removeEventListener("keydown", this._handleKeyDown);
    }

    this.initialized = false;
  }

  /**
   * Get current configuration
   * @returns {Object} Current options
   */
  getConfig() {
    return { ...this.options };
  }

  /**
   * Update configuration
   * @param {Object} options - New options
   */
  updateConfig(options) {
    this.options = { ...this.options, ...options };

    // Reinitialize with new options
    if (this.initialized) {
      const selectedDate = this.getDate();
      this.destroy();
      this.initialize(this.container);

      if (selectedDate) {
        this.setDate(selectedDate);
      }
    }
  }
}

export default DatePicker;
